import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { Subject, Subscription } from 'rxjs';
import { ActivityFiltersPayload } from 'src/app/components/shared/activity-filters/activity-filters.interface';
import {
    DrillDownDocumentRow,
    DrillDownPair,
    DrillDownPayload,
    DrillDownResponse,
    RegistruEntry,
} from '../../raport-activitate-detaliat.interface';
import { RaportActivitateDetaliatService } from '../../raport-activitate-detaliat.service';

@Component({
    selector: 'app-matrix-drill-down-table',
    templateUrl: './matrix-drill-down-table.component.html',
    styleUrls: ['./matrix-drill-down-table.component.scss'],
})
export class MatrixDrillDownTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() filters!: ActivityFiltersPayload;
    @Input() pair!: DrillDownPair;
    @Input() canViewDocument = false;
    /** Filtru de tip document venit din click pe chip-ul de matrice (suprascrie filtrul general doar pentru acest drill). */
    @Input() tipDocumentFilter: number | null = null;

    @Output() openDocument = new EventEmitter<DrillDownDocumentRow>();

    @ViewChild(DataTableDirective, { static: false }) dtElement?: DataTableDirective;

    dtOptions: DataTables.Settings = {};
    dtTrigger: Subject<DataTables.Settings> = new Subject();

    isLoading = false;
    private dtInitialized = false;
    private lastPairKey = '';
    private lastFiltersHash = '';
    private subs: Subscription[] = [];

    constructor(private service: RaportActivitateDetaliatService) { }

    ngOnInit(): void {
        this.dtOptions = {
            pagingType: 'full_numbers',
            pageLength: 25,
            serverSide: true,
            processing: true,
            searching: false,
            ordering: true,
            order: [[1, 'desc']],
            language: {
                emptyTable: 'Nu există documente pentru perechea selectată.',
                info: 'Afișează _START_ - _END_ din _TOTAL_ documente',
                infoEmpty: '0 documente',
                infoFiltered: '(filtrat din _MAX_ documente)',
                lengthMenu: 'Afișează _MENU_ documente per pagină',
                loadingRecords: 'Se încarcă...',
                processing: 'Se procesează...',
                zeroRecords: 'Niciun document găsit.',
                paginate: { first: '«', last: '»', next: '›', previous: '‹' },
            },
            ajax: (dataTablesParameters: any, callback) => {
                this.isLoading = true;
                const orderRaw = dataTablesParameters.order ?? [];
                const colsRaw = dataTablesParameters.columns ?? [];
                const order = orderRaw.map((o: any) => ({
                    column: this.columnIndexToName(o.column, colsRaw),
                    dir: o.dir as 'asc' | 'desc',
                }));

                const info = { ...this.filters.info };
                if (this.tipDocumentFilter != null) {
                    info.tip_document_id = this.tipDocumentFilter;
                }

                const payload: DrillDownPayload = {
                    ...this.filters,
                    info,
                    pair: this.pair,
                    pagination: {
                        start: dataTablesParameters.start ?? 0,
                        length: dataTablesParameters.length ?? 25,
                        order,
                    },
                    draw: dataTablesParameters.draw ?? 1,
                };

                this.service.getDrillDownDocuments(payload).subscribe({
                    next: (response: DrillDownResponse) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: response.recordsTotal,
                            recordsFiltered: response.recordsFiltered,
                            data: response.data,
                            draw: response.draw,
                        });
                    },
                    error: (err) => {
                        this.isLoading = false;
                        console.error('Error loading drill-down documents:', err);
                        callback({ recordsTotal: 0, recordsFiltered: 0, data: [], draw: dataTablesParameters.draw });
                    },
                });
            },
            columns: [
                {
                    data: null,
                    orderable: true,
                    name: 'reg_number',
                    render: (_data: any, _type: any, row: DrillDownDocumentRow) =>
                        this.renderRegistruCell(row),
                },
                { data: 'reg_date', orderable: true, name: 'reg_date' },
                { data: 'categorie_name', orderable: false, name: 'id_categorie' },
                { data: 'tip_document_name', orderable: false, name: 'id_tip_document' },
                {
                    data: 'source_name',
                    orderable: false,
                    render: (d: any) => (d ? this.escapeHtml(String(d)) : '<span class="text-muted small">—</span>'),
                },
                { data: 'current_status_name', orderable: false, name: 'current_status' },
                {
                    data: null,
                    orderable: false,
                    render: (_data: any, _type: any, row: DrillDownDocumentRow) => this.renderTermBadge(row),
                },
                {
                    data: 'deadline_date',
                    orderable: false,
                    render: (d: any) =>
                        d ? this.escapeHtml(String(d).substring(0, 10)) : '<span class="text-muted small">—</span>',
                },
                { data: 'observatii', orderable: false },
                { data: 'last_activity_from', orderable: true, name: 'last_activity_date' },
                { data: null, orderable: false },
            ],
            columnDefs: [
                {
                    targets: -1,
                    render: () => {
                        if (!this.canViewDocument) {
                            return '<span class="text-muted small">—</span>';
                        }
                        return '<button class="btn btn-sm btn-info open-doc-btn" title="Vizualizare document">' +
                            '<i class="fa fa-eye"></i></button>';
                    },
                },
            ],
            rowCallback: (row: Node, data: object | any[]) => {
                if (!this.canViewDocument) {
                    (row as HTMLElement).style.cursor = 'default';
                    return;
                }
                (row as HTMLElement).style.cursor = 'pointer';
                row.addEventListener('click', () => this.openDocument.emit(data as DrillDownDocumentRow));
            },

        };
    }

    ngAfterViewInit(): void {
        this.lastPairKey = this.computePairKey(this.pair);
        this.lastFiltersHash = this.computeFiltersHash(this.filters);
        this.dtTrigger.next(this.dtOptions);
        this.dtInitialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.dtInitialized) {
            return;
        }
        const pairKey = this.computePairKey(this.pair);
        const filtersHash = this.computeFiltersHash(this.filters);
        if (pairKey === this.lastPairKey && filtersHash === this.lastFiltersHash) {
            return;
        }
        this.lastPairKey = pairKey;
        this.lastFiltersHash = filtersHash;
        this.reloadTable();
    }

    private computePairKey(pair: DrillDownPair | undefined): string {
        if (!pair) return '';
        return `${pair.origin_level}:${pair.origin_id ?? 'null'}|${pair.destination_level}:${pair.destination_id ?? 'null'}`;
    }

    private computeFiltersHash(filters: ActivityFiltersPayload | undefined): string {
        const tip = `|tip:${this.tipDocumentFilter ?? ''}`;
        if (!filters) return tip;
        try {
            return JSON.stringify(filters) + tip;
        } catch {
            return tip;
        }
    }

    ngOnDestroy(): void {
        this.dtTrigger.complete();
        this.subs.forEach((s) => s.unsubscribe());
    }

    private renderTermBadge(row: DrillDownDocumentRow): string {
        if (!row.term_status || row.term_status === 'na') {
            return '<span class="text-muted small">—</span>';
        }
        const color = this.escapeHtml(row.term_status_color || '#777777');
        const label = this.escapeHtml(row.term_status_label || '');
        const days = row.days_remaining;
        let title = '';
        if (days !== null && days !== undefined) {
            title = days < 0 ? `Întârziat cu ${Math.abs(days)} zile` : `${days} zile rămase`;
        }
        return `<span class="term-badge" style="background:${color}" title="${this.escapeHtml(title)}">${label}</span>`;
    }

    private renderRegistruCell(row: DrillDownDocumentRow): string {
        const entries = row.registru_entries ?? [];

        if (entries.length === 0) {
            const number = row.reg_number !== null && row.reg_number !== undefined ? String(row.reg_number) : '—';
            return `<div class="registru-line">${this.formatRegistruInline(null, number)}</div>`;
        }

        return entries
            .map((e: RegistruEntry) => {
                const number = e.reg_number !== null && e.reg_number !== undefined ? String(e.reg_number) : '—';
                const dateLabel = e.reg_date ? this.escapeHtml(e.reg_date.substring(0, 10)) : '';
                const datePart = dateLabel ? `<span class="registru-date">${dateLabel}</span>` : '';
                return `<div class="registru-line">${this.formatRegistruInline(e.serie_prefix, number)}${datePart}</div>`;
            })
            .join('');
    }

    /**
     * Format final: "<prefix>. <number>" (ex. "R. 12345").
     * Prefix-ul în BD poate veni cu sau fără punct — normalizăm: scoatem punctul din coadă, adăugăm noi unul.
     */
    private formatRegistruInline(prefixRaw: string | null, number: string): string {
        const numberSafe = this.escapeHtml(number);
        if (!prefixRaw) {
            return `<span class="registru-text"><span class="registru-number">${numberSafe}</span></span>`;
        }
        const prefix = prefixRaw.replace(/\.+$/, '').trim();
        const prefixSafe = this.escapeHtml(prefix);
        return `<span class="registru-text"><span class="registru-prefix">${prefixSafe}.</span> <span class="registru-number">${numberSafe}</span></span>`;
    }

    private escapeHtml(s: string): string {
        return s.replace(/[&<>"']/g, (ch) => {
            switch (ch) {
                case '&': return '&amp;';
                case '<': return '&lt;';
                case '>': return '&gt;';
                case '"': return '&quot;';
                case "'": return '&#39;';
            }
            return ch;
        });
    }

    private columnIndexToName(idx: number, cols: any[]): string {
        const fallback = 'reg_date';
        if (!cols || !cols[idx]) return fallback;
        return cols[idx].name || cols[idx].data || fallback;
    }

    private reloadTable(): void {
        const dtInstancePromise = this.dtElement?.dtInstance;
        if (!dtInstancePromise || typeof (dtInstancePromise as any).then !== 'function') {
            return;
        }
        (dtInstancePromise as Promise<DataTables.Api>).then((dtInstance) => {
            dtInstance.ajax.reload();
        });
    }
}
